import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiUsers, FiUserCheck, FiPlus, FiSave, FiChevronLeft, FiRotateCcw } from 'react-icons/fi';
import MobileSubNavigator from '../../../components/MobileSubNavigator';
import { getHomeLinks } from '../../../utils/homeLinks';
import { tokens, Badge, Tag, Button, Input, SearchInput, Select, Checkbox, EmptyState } from '../../../components/admin/AdminUI';

const c = tokens.color;
const API = '/request/perm';
const CAT_TITLE = { board: '게시판 관리', user: '사용자 관리', system: '시스템 관리', content: '컨텐츠 관리', file: '파일 관리', report: '리포트 관리' };

const Container = styled.div` width: 100%; padding: 1rem; background: ${c.appBg}; min-height: 100vh; box-sizing: border-box; font-family: ${tokens.font}; color: ${c.text}; `;
const Tabs = styled.div` display: flex; gap: 4px; background: ${c.surfaceMuted}; border: 1px solid ${c.border}; border-radius: ${tokens.radius.md}; padding: 3px; margin-bottom: 14px; `;
const Tab = styled.button` flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 6px; border: none; cursor: pointer; font-family: inherit; font-size: 14px; font-weight: 600; padding: 9px 0; border-radius: ${tokens.radius.sm}; background: ${(p) => (p.$active ? '#fff' : 'transparent')}; color: ${(p) => (p.$active ? c.primary : c.muted)}; box-shadow: ${(p) => (p.$active ? tokens.shadow.xs : 'none')}; & svg { width: 15px; height: 15px; } `;
const ItemCard = styled.button` display: flex; align-items: center; justify-content: space-between; gap: 8px; width: 100%; text-align: left; background: ${c.surface}; border: 1px solid ${c.border}; border-radius: ${tokens.radius.lg}; box-shadow: ${tokens.shadow.xs}; padding: 13px 14px; margin-bottom: 8px; font-family: inherit; cursor: pointer; `;
const Name = styled.div` font-size: 15px; font-weight: 700; color: ${c.heading}; `;
const Sub = styled.div` font-size: 12px; color: ${c.muted}; margin-top: 2px; `;
const Section = styled.div` background: ${c.surface}; border: 1px solid ${c.border}; border-radius: ${tokens.radius.lg}; box-shadow: ${tokens.shadow.card}; margin-bottom: 12px; overflow: hidden; `;
const SecHead = styled.div` display: flex; align-items: center; justify-content: space-between; padding: 11px 14px; background: ${c.surfaceMuted}; border-bottom: 1px solid ${c.border}; font-size: 13px; font-weight: 700; color: ${c.heading}; `;
const PermRow = styled.label` display: flex; align-items: center; gap: 10px; padding: 12px 14px; border-bottom: 1px solid ${c.borderSubtle}; font-size: 14px; &:last-child { border-bottom: none; } `;
const Bar = styled.div` display: flex; align-items: center; gap: 8px; margin-bottom: 12px; `;
const AddBar = styled.div` display: flex; gap: 8px; margin-bottom: 14px; `;

export default function MobileOrganizationManagement() {
  const [tab, setTab] = useState('dept');
  const [catalog, setCatalog] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selDept, setSelDept] = useState(null);
  const [deptGrants, setDeptGrants] = useState({});
  const [newDeptName, setNewDeptName] = useState('');
  const [acctSearch, setAcctSearch] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [selAcct, setSelAcct] = useState(null);
  const [acctPerms, setAcctPerms] = useState([]);
  const [overrides, setOverrides] = useState({});

  const cats = [...new Set(catalog.map((p) => p.category))];
  const byCat = (cat) => catalog.filter((p) => p.category === cat);

  useEffect(() => {
    (async () => {
      try {
        const [cat, dep] = await Promise.all([
          fetch(`${API}/catalog`, { credentials: 'include' }).then((r) => r.json()),
          fetch(`${API}/departments`, { credentials: 'include' }).then((r) => r.json()),
        ]);
        if (Array.isArray(cat)) setCatalog(cat);
        if (Array.isArray(dep)) setDepartments(dep);
      } catch (e) {}
    })();
  }, []);

  const selectDept = async (d) => {
    setSelDept(d);
    try { const m = await fetch(`${API}/department/grants?dept_id=${d.id}`, { credentials: 'include' }).then((r) => r.json()); setDeptGrants(m || {}); } catch (e) { setDeptGrants({}); }
  };
  const saveDept = async () => {
    try { await fetch(`${API}/department/grants/save`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dept_id: selDept.id, grants: deptGrants }) }); alert('저장되었습니다.'); } catch (e) { alert('저장 실패'); }
  };
  const addDept = async () => {
    if (!newDeptName.trim()) return;
    try { await fetch(`${API}/department/create`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: newDeptName }) }); setNewDeptName(''); const dep = await fetch(`${API}/departments`, { credentials: 'include' }).then((r) => r.json()); setDepartments(dep); } catch (e) {}
  };

  const loadAccounts = useCallback(async (search) => {
    try { const q = search ? `?search=${encodeURIComponent(search)}` : ''; const rows = await fetch(`${API}/accounts${q}`, { credentials: 'include' }).then((r) => r.json()); if (Array.isArray(rows)) setAccounts(rows); } catch (e) { setAccounts([]); }
  }, []);
  useEffect(() => { if (tab === 'account') loadAccounts(''); }, [tab, loadAccounts]);

  const selectAcct = async (a) => {
    setSelAcct(a); setOverrides({});
    try { const rows = await fetch(`${API}/account/permissions?user_id=${encodeURIComponent(a.user_id)}`, { credentials: 'include' }).then((r) => r.json()); setAcctPerms(Array.isArray(rows) ? rows : []); } catch (e) { setAcctPerms([]); }
  };
  const aRow = (k) => acctPerms.find((r) => r.perm_key === k) || {};
  const dispEff = (k) => (k in overrides ? (overrides[k] === null ? (aRow(k).dept_granted || false) : overrides[k]) : !!aRow(k).effective);
  const overridden = (k) => (k in overrides ? overrides[k] !== null : aRow(k).user_granted !== null && aRow(k).user_granted !== undefined);
  const changeDept = async (deptId) => {
    try {
      await fetch(`${API}/account/dept`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_id: selAcct.user_id, dept_id: deptId || null }) });
      const a = { ...selAcct, perm_dept_id: deptId || null }; setSelAcct(a); loadAccounts(acctSearch);
      const rows = await fetch(`${API}/account/permissions?user_id=${encodeURIComponent(a.user_id)}`, { credentials: 'include' }).then((r) => r.json()); setAcctPerms(Array.isArray(rows) ? rows : []); setOverrides({});
    } catch (e) {}
  };
  const saveAcct = async () => {
    const payload = Object.entries(overrides).map(([perm_key, granted]) => ({ perm_key, granted }));
    if (!payload.length) { alert('변경사항이 없습니다.'); return; }
    try { await fetch(`${API}/account/override`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_id: selAcct.user_id, overrides: payload }) });
      const rows = await fetch(`${API}/account/permissions?user_id=${encodeURIComponent(selAcct.user_id)}`, { credentials: 'include' }).then((r) => r.json()); setAcctPerms(Array.isArray(rows) ? rows : []); setOverrides({}); alert('저장되었습니다.'); } catch (e) { alert('저장 실패'); }
  };

  const deptOptions = [{ value: '', label: '소속 없음' }, ...departments.map((d) => ({ value: String(d.id), label: d.name }))];

  return (
    <MobileSubNavigator links={getHomeLinks()} sectionTitle="권한관리" backPath="/" showBackButton={true}>
      <Container>
        <Tabs>
          <Tab $active={tab === 'dept'} onClick={() => { setTab('dept'); setSelAcct(null); }}><FiUsers /> 부서별</Tab>
          <Tab $active={tab === 'account'} onClick={() => { setTab('account'); setSelDept(null); }}><FiUserCheck /> 계정별</Tab>
        </Tabs>

        {tab === 'dept' && !selDept && (
          <>
            <AddBar>
              <Input placeholder="새 부서명" value={newDeptName} onChange={(e) => setNewDeptName(e.target.value)} />
              <Button $variant="primary" onClick={addDept}><FiPlus /></Button>
            </AddBar>
            {departments.length === 0 ? <EmptyState title="부서가 없습니다" /> : departments.map((d) => (
              <ItemCard key={d.id} onClick={() => selectDept(d)}>
                <span style={{ paddingLeft: d.level * 10 }}><Name>{d.name}</Name><Sub>{d.description}</Sub></span>
                <Badge $tone="neutral">{d.member_count}명</Badge>
              </ItemCard>
            ))}
          </>
        )}

        {tab === 'dept' && selDept && (
          <>
            <Bar>
              <Button $variant="ghost" $size="sm" onClick={() => setSelDept(null)}><FiChevronLeft /> 목록</Button>
              <strong style={{ flex: 1 }}>{selDept.name}</strong>
              <Button $variant="primary" $size="sm" onClick={saveDept}><FiSave /> 저장</Button>
            </Bar>
            {cats.map((cat) => (
              <Section key={cat}>
                <SecHead>{CAT_TITLE[cat] || cat}</SecHead>
                {byCat(cat).map((p) => (
                  <PermRow key={p.perm_key}>
                    <Checkbox checked={!!deptGrants[p.perm_key]} onChange={() => setDeptGrants((g) => ({ ...g, [p.perm_key]: !g[p.perm_key] }))} />
                    <span style={{ flex: 1 }}>{p.label}</span>
                  </PermRow>
                ))}
              </Section>
            ))}
          </>
        )}

        {tab === 'account' && !selAcct && (
          <>
            <AddBar>
              <SearchInput placeholder="아이디/이름 검색" value={acctSearch} onChange={(e) => setAcctSearch(e.target.value)} onSearch={() => loadAccounts(acctSearch)} grow="1 1 auto" />
            </AddBar>
            {accounts.length === 0 ? <EmptyState title="계정이 없습니다" /> : accounts.map((a) => (
              <ItemCard key={a.user_id} onClick={() => selectAcct(a)}>
                <span><Name>{a.user_name || a.user_id}</Name><Sub>{a.user_id}{a.user_rank ? ` · ${a.user_rank}` : ''}</Sub></span>
                {a.dept_name ? <Badge $tone="brand">{a.dept_name}</Badge> : <Tag>미배정</Tag>}
              </ItemCard>
            ))}
          </>
        )}

        {tab === 'account' && selAcct && (
          <>
            <Bar>
              <Button $variant="ghost" $size="sm" onClick={() => setSelAcct(null)}><FiChevronLeft /> 목록</Button>
              <strong style={{ flex: 1 }}>{selAcct.user_name || selAcct.user_id}</strong>
              <Button $variant="primary" $size="sm" onClick={saveAcct}><FiSave /> 저장</Button>
            </Bar>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 13, color: c.muted }}>소속</span>
              <Select value={selAcct.perm_dept_id ? String(selAcct.perm_dept_id) : ''} onChange={changeDept} options={deptOptions} grow="1 1 auto" minWidth="100%" />
            </div>
            {cats.map((cat) => (
              <Section key={cat}>
                <SecHead>{CAT_TITLE[cat] || cat}</SecHead>
                {byCat(cat).map((p) => (
                  <PermRow key={p.perm_key}>
                    <Checkbox checked={dispEff(p.perm_key)} onChange={() => setOverrides((o) => ({ ...o, [p.perm_key]: !dispEff(p.perm_key) }))} />
                    <span style={{ flex: 1 }}>{p.label}</span>
                    {overridden(p.perm_key) ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Badge $tone="warning">개별</Badge>
                        <Button $variant="ghost" $size="sm" onClick={() => setOverrides((o) => ({ ...o, [p.perm_key]: null }))}><FiRotateCcw /></Button>
                      </span>
                    ) : <Tag>상속</Tag>}
                  </PermRow>
                ))}
              </Section>
            ))}
          </>
        )}
      </Container>
    </MobileSubNavigator>
  );
}
